<app-header />

<div class="container" style="padding-top:0;">
  <mat-card>
    <mat-card-title>Create new export</mat-card-title>
    <mat-card-content>
      <p class="info">
        Complete the form below to schedule a new export based on your selections. The export
        process will run in the background and the generated export file will appear on the export
        list screen when it has completed.
      </p>
      <mat-vertical-stepper
        #stepper
        *ngIf="hasExportPermission"
        (selectionChange)="onStepChange($event)"
      >
        <mat-step [stepControl]="typeForm">
          <p class="info">
            The export type controls what is exported into each row of the exported data.
          </p>
          <ul class="info">
            <li><b>Sample</b>: One row per sample</li>
            <li><b>Measurement</b>: One row per measurement</li>
            <li><b>Monthly average</b>: One row per month</li>
          </ul>
          <br />
          <form [formGroup]="typeForm">
            <ng-template matStepLabel>Choose export type</ng-template>
            <mat-form-field>
              <mat-label>Export type</mat-label>
              <mat-select formControlName="type" (selectionChange)="onTypeChange($event)">
                <mat-option value="sample">Sample</mat-option>
                <mat-option value="measurement">Measurement</mat-option>
                <mat-option value="monthly-average">Monthly average</mat-option>
              </mat-select>
            </mat-form-field>
          </form>
        </mat-step>

        <mat-step [stepControl]="filterForm">
          <ng-template matStepLabel>Select taxa</ng-template>
          <form [formGroup]="filterForm">
            <p class="info">Choose one way to select taxa:</p>
            <mat-radio-group
              class="taxa-selection-mode"
              formControlName="taxaSelectionMode"
              (change)="onTaxaSelectionModeChange()"
            >
              <mat-radio-button value="countingMethods">Counting categories</mat-radio-button>
              <mat-radio-button value="taxonIds">Taxon IDs</mat-radio-button>
            </mat-radio-group>

            <div class="taxa-selection-panel">
              <div *ngIf="isCountingMethodsMode">
                <p class="info">Choose which counting categories to include:</p>
                <mat-checkbox
                  formControlName="eyeCountCountingMethod"
                  (change)="onCountingMethodChange()"
                >
                  Eye count
                </mat-checkbox>
                <mat-checkbox
                  formControlName="traverseCountingMethod"
                  (change)="onCountingMethodChange()"
                >
                  Traverse
                </mat-checkbox>
                <mat-checkbox
                  formControlName="phytoCountingMethod"
                  (change)="onCountingMethodChange()"
                >
                  Phyto
                </mat-checkbox>
              </div>

              <div *ngIf="isTaxonIdsMode">
                <p class="info">Enter a comma separated list of taxon IDs.</p>
                <mat-form-field class="text-area-field">
                  <mat-label>Taxon IDs</mat-label>
                  <textarea
                    matInput
                    formControlName="cprTaxonIds"
                    (input)="onTaxonIdsChange()"
                  ></textarea>
                </mat-form-field>

                <div class="export-help">
                  <p class="help-box-title">Taxon ID filtering guidance</p>
                  <p>
                    <strong>Use only the latest accepted taxon IDs in this field.</strong>
                    Measurements are first matched to their latest accepted taxon before filtering, so filtering on their older ids won't include them.
                    If an accepted id chain is <strong>1 -> 2 -> 3</strong>, enter <strong>3</strong>, not 1 or 2.
                  </p>
                </div>
              </div>
            </div>

            <div class="confirm-errors" *ngIf="showTaxaStepError">
              <p>{{ taxaSelectionErrorMessage }}</p>
            </div>
          </form>
        </mat-step>
        <mat-step [stepControl]="filterForm" #geoStep>
          <ng-template matStepLabel>Select location</ng-template>
          <p class="info">Define the geographic area to filter on.</p>
          <p class="info">Important notes:</p>
          <div class="info">
            <ul>
              <li>
                Choose one location mode. Changing the mode clears any values from the other modes.
              </li>
              <li>
                For a full list of predefined areas and their coordinates, see the geographic areas
                list here: (link to be provided).
              </li>
              <li>
                Custom polygon coordinates should be entered as lat,lng decimal-degree pairs
                separated by semicolons or new lines, with up to 8 decimal places. Latitude
                must be between -90 and 90 and longitude between -180 and 180.
              </li>
              <li>For tow route or ID searches, use the same shorthand as the tow list, including X- for single-letter routes.</li>
            </ul>
          </div>
          <br />
          <div class="geo-form-container">
            <div class="geo-form-inner">
              <form [formGroup]="filterForm">
                <p class="info">Choose one location mode:</p>
                <mat-radio-group
                  class="selection-mode"
                  formControlName="locationMode"
                  (change)="onLocationModeChange()"
                >
                  <mat-radio-button [value]="predefinedAreaLocationMode">Predefined areas</mat-radio-button>
                  <mat-radio-button [value]="customAreaLocationMode">Custom area</mat-radio-button>
                  <mat-radio-button [value]="towsLocationMode">Tows</mat-radio-button>
                </mat-radio-group>

                <div class="selection-panel location-selection-panel">
                  <mat-form-field class="multi-select-field" *ngIf="isPredefinedAreaMode">
                    <mat-label>Predefined areas:</mat-label>
                    <mat-select
                      formControlName="geoCodes"
                      multiple
                      (selectionChange)="predefinedGeographicRegionChange()"
                    >
                      <mat-option *ngFor="let geoArea of geoAreas" [value]="geoArea[0]">{{
                        geoArea[1]
                      }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <div class="custom-area-panel" *ngIf="isCustomAreaMode">
                    <mat-form-field class="text-area-field">
                      <mat-label>Custom area (decimal degrees): enter lat,lng pairs separated by ; or a newline.</mat-label>
                      <textarea
                        matInput
                        formControlName="polygonStr"
                        placeholder="5.00000000,2.00000000; 3.00000000,2.00000000; 3.00000000,-2.00000000"
                        (keyup)="customGeographicRegionChange()"
                        (blur)="validatePolygonInput()"
                      ></textarea>
                      <mat-hint>Use decimal degrees with up to 8 decimal places. The polygon is closed automatically if you omit the final point.</mat-hint>
                      <mat-error *ngIf="polygonErrorMessage">{{ polygonErrorMessage }}</mat-error>
                    </mat-form-field>

                    <div class="custom-area-preview">
                      <div
                        class="custom-area-map"
                        leaflet
                        [leafletOptions]="options"
                        (leafletMapReady)="onMapReady($event)"
                        *ngIf="mapVisible"
                      ></div>
                      <button mat-flat-button color="primary" (click)="drawPolygon()">
                        Draw polygon
                      </button>
                    </div>
                  </div>

                  <mat-form-field class="text-area-field" *ngIf="isTowsMode">
                    <mat-label>By Tow Route/ID (Use X- for single letter)</mat-label>
                    <textarea
                      matInput
                      formControlName="towSearchTermsText"
                      placeholder="Enter comma or newline separated tow routes / IDs"
                      (keyup)="towSearchTermsChange()"
                    ></textarea>
                    <mat-hint>Examples: 12AB, 7X-, 31CD</mat-hint>
                  </mat-form-field>
                </div>

                <div class="confirm-errors" *ngIf="showLocationStepError">
                  <p>{{ locationSelectionErrorMessage }}</p>
                </div>
              </form>
            </div>
          </div>
        </mat-step>
        <mat-step [stepControl]="filterForm">
          <ng-template matStepLabel>Select date range</ng-template>
          <p class="info">Set the date range to apply to the export.</p>

          <form [formGroup]="filterForm">
            <mat-form-field>
              <mat-label>From date</mat-label>
              <input matInput [matDatepicker]="fromDate" formControlName="fromDate" />
              <mat-hint>{{ dateFormats.parse.dateInput[0] }}</mat-hint>
              <mat-datepicker-toggle matIconSuffix [for]="fromDate"></mat-datepicker-toggle>
              <mat-datepicker #fromDate></mat-datepicker>
            </mat-form-field>

            <mat-form-field>
              <mat-label>To date</mat-label>
              <input matInput [matDatepicker]="toDate" formControlName="toDate" />
              <mat-hint>{{ dateFormats.parse.dateInput[0] }}</mat-hint>
              <mat-datepicker-toggle matIconSuffix [for]="toDate"></mat-datepicker-toggle>
              <mat-datepicker #toDate></mat-datepicker>
            </mat-form-field>

            <div class="confirm-errors" *ngIf="showDateStepError">
              <p>{{ fromDateErrorMessage }}</p>
            </div>
          </form>
        </mat-step>
        <mat-step [stepControl]="filterForm">
          <p class="info">
            Choose whether to include non-standard data.
            <br />
            Note that unfinalised data is not usually suitable for sharing externally.
          </p>
          <br />
          <form [formGroup]="filterForm">
            <ng-template matStepLabel>Include non-standard data?</ng-template>
            <mat-checkbox formControlName="includeUnfinalised"
              >Include unfinalised data?</mat-checkbox
            >
          </form>
        </mat-step>

        <mat-step [stepControl]="filterForm">
          <p class="info"></p>
          <br />
          <form [formGroup]="filterForm">
            <ng-template matStepLabel>Additional Information</ng-template>
            <ng-template matStepLabel>Spread date field?</ng-template>
            <mat-checkbox formControlName="spreadDate">Spread date field?</mat-checkbox>
            <ng-template matStepLabel>Include sun parameters?</ng-template>
            <br />
            <mat-checkbox formControlName="includeSunParameters"
              >Include sun parameters?</mat-checkbox
            >
            <br />
            <mat-checkbox formControlName="includePCIValues"
              >Include PCI (Chlorophyl Index) values?</mat-checkbox
            >
          </form>
        </mat-step>

        <mat-step [stepControl]="filterForm">
          <p class="info">
            Choose whether to export the raw measured value or one of the mapped values of the
            counting category
          </p>
          <br />
          <form [formGroup]="filterForm">
            <ng-template matStepLabel>Choose measurement value</ng-template>
            <mat-form-field>
              <mat-label>Choose measurement value</mat-label>
              <mat-select formControlName="valueMode">
                <mat-option
                  *ngFor="let valueModeLable of valueModeLables"
                  [value]="valueModeLable[0]"
                  >{{ valueModeLable[1] }}</mat-option
                >
              </mat-select>
            </mat-form-field>
            <p>Plussed values output mode</p>
            <mat-radio-group formControlName="plussedValuesExportMode">
              <mat-radio-button
                *ngFor="let option of plussedValuesExportModeLabels"
                [value]="option[0]"
              >
                <ng-container *ngIf="option[0] === 'ten-to-minus-ten'; else plainPlussedModeLabel">
                  Export as 10<sup>-10</sup>
                </ng-container>
                <ng-template #plainPlussedModeLabel>
                  {{ option[1] }}
                </ng-template>
              </mat-radio-button>
            </mat-radio-group>
          </form>
        </mat-step>

        <!-- <mat-step>
          <ng-template matStepLabel>JSON</ng-template>
          {{ filterForm.value | json }}
          {{ typeForm.controls['type'].value }}
        </mat-step> -->
        <mat-step [stepControl]="filterForm">
          <ng-template matStepLabel>Confirm & start export</ng-template>
          <p class="info">
            Please review your selections and then click "Start export" to begin the export.
          </p>
          <div class="info">
            <ul>
              <li>
                Export type: <b>{{ typeForm.controls['type'].value | friendlyLabel }}</b>
              </li>
              <li *ngIf="isTaxonIdsMode && filterForm.controls['cprTaxonIds'].value?.length">
                Taxon IDs: <b>{{ filterForm.controls['cprTaxonIds'].value }}</b>
              </li>
              <li *ngIf="isCountingMethodsMode && filterForm.controls['eyeCountCountingMethod'].value">
                All Eye count values are included.
              </li>
              <li *ngIf="isCountingMethodsMode && filterForm.controls['traverseCountingMethod'].value">
                All Traverse values are included.
              </li>
              <li *ngIf="isCountingMethodsMode && filterForm.controls['phytoCountingMethod'].value">
                All Phyto values are included.
              </li>
              <li
                *ngIf="
                  filterForm.controls['locationMode'].value === predefinedAreaLocationMode &&
                  filterForm.controls['geoCodes'].value &&
                  filterForm.controls['geoCodes'].value.length
                "
              >
                Predefined geographic areas: <b>{{ filterForm.controls['geoCodes'].value }}</b>
              </li>
              <li
                *ngIf="
                  filterForm.controls['locationMode'].value === customAreaLocationMode &&
                  filterForm.controls['polygonStr'].value
                "
              >
                Custom area: <b>{{ filterForm.controls['polygonStr'].value }}</b>
              </li>
              <li
                *ngIf="
                  filterForm.controls['locationMode'].value === towsLocationMode &&
                  filterForm.controls['towSearchTermsText'].value
                "
              >
                Tow routes / IDs: <b>{{ filterForm.controls['towSearchTermsText'].value }}</b>
              </li>
              <li *ngIf="filterForm.controls['fromDate'].value">
                From date:
                <b>{{ filterForm.controls['fromDate'].value.toString() | date: 'dd/MM/yyyy' }}</b>
              </li>
              <li *ngIf="filterForm.controls['toDate'].value">
                To date:
                <b>{{ filterForm.controls['toDate'].value.toString() | date: 'dd/MM/yyyy' }}</b>
              </li>
              <li>
                Include unfinalised?:
                <b>{{ filterForm.controls['includeUnfinalised'].value == true ? 'YES' : 'NO' }}</b>
              </li>
              <li>
                Spread date field?:
                <b>{{ filterForm.controls['spreadDate'].value == true ? 'YES' : 'NO' }}</b>
              </li>
              <li>
                Include sun parameters?:
                <b>{{
                  filterForm.controls['includeSunParameters'].value == true ? 'YES' : 'NO'
                }}</b>
              </li>
              <li>
                Include PCI values?:
                <b>{{ filterForm.controls['includePCIValues'].value == true ? 'YES' : 'NO' }}</b>
              </li>
              <li>
                Value mode: <b>{{ filterForm.controls['valueMode'].value }}</b>
              </li>
              <li>
                Plussed values output mode:
                <b>{{ filterForm.controls['plussedValuesExportMode'].value }}</b>
              </li>
            </ul>
            <div class="confirm-errors" *ngIf="confirmStepErrors.length">
              <p>Please fix the following before starting the export:</p>
              <ul>
                <li *ngFor="let errorMessage of confirmStepErrors">{{ errorMessage }}</li>
              </ul>
            </div>
          </div>
          <div class="controls">
            <button
              mat-flat-button
              color="primary"
              (click)="startExport()"
              [disabled]="isProcessing || hasKnownBlockingValidationErrors"
            >
              Start export
            </button>
          </div>
        </mat-step>
      </mat-vertical-stepper>
      <p *ngIf="!hasExportPermission">You do not have permission to export.</p>
    </mat-card-content>
  </mat-card>
</div>
